import Loading from '../common/loading.tsx';
import Timer from './timer.tsx';
import { useNavigate } from 'react-router-dom';
import { Dispatch, SetStateAction, useState } from 'react';
import { GetTestAttemptQuery } from '../../apollo/__generated__/graphql.ts';
import MarkerLightDownIcon from '../../assets/icons/marker-light-down.svg?react';
import PinnedNoteIcon from '../../assets/icons/pinned-note.svg?react';
import { cn } from '../../utils/common-utils.ts';
import { useHighlight } from '../../contexts/highlight-provider/useHighlight.ts';
import Modal from '../common/modal.tsx';
import BluebookCalculatorIcon from '../common/icons/bluebook-calculator-icon.tsx';
import BluebookReferenceIcon from '../common/icons/bluebook-reference-icon.tsx';
import QuestionReportForm from '../questions/question-content/question-report-form.tsx';
import ReferenceModal from '../questions/question-content/reference-modal.tsx';
import AttemptMoreMenu from '../common/attempt-more-menu.tsx';

interface IProps {
  setSeconds: Dispatch<SetStateAction<number>>;
  isLoading: boolean;
  openDirections: () => void;
  attempt: GetTestAttemptQuery['testAttempt']['getAttempt'];
  seconds: number;
  onTimerOver: () => void;
  newTimer?: boolean;
  setNewTimer?: Dispatch<SetStateAction<boolean>>;
  stopTimer?: boolean;
  setStopTimer?: Dispatch<SetStateAction<boolean>>;
  questionId?: number;
  questions: GetTestAttemptQuery['testAttempt']['getAttempt']['lastModule']['questions'];
  openQuestionsModal: () => void;
  currentQuestionId: number;
  isMath: boolean;
  isCalc: boolean;
  onToggleCalculator: () => void;
}

const toolbarButtonClassName =
  'mx-0.5 flex h-[4rem] w-[4.4rem] flex-col items-center justify-center rounded-md border border-transparent bg-transparent px-1.5 text-[#222] transition-colors hover:bg-[rgba(254,219,0,0.18)] focus:border-none focus:outline-none';

const activeToolbarButtonClassName =
  'underline decoration-current underline-offset-4';

const toolButtonIconRowClassName = 'flex h-7 items-center justify-center';

const toolButtonLabelClassName =
  'mt-1.5 whitespace-nowrap text-xs font-semibold leading-none';

const attemptChromeDashStyle = {
  backgroundImage:
    'linear-gradient(to right, #111111 0 72%, transparent 72% 100%)',
  backgroundRepeat: 'repeat-x',
  backgroundSize: 'calc(100% / 56) 100%',
} as const;

const ReferenceToolbarIcon = () => (
  <BluebookReferenceIcon className="translate-y-px" />
);

const TestAttemptHeader = ({
  setSeconds,
  isLoading,
  openDirections,
  attempt,
  seconds,
  onTimerOver,
  newTimer,
  setNewTimer,
  stopTimer,
  setStopTimer,
  questionId,
  questions,
  currentQuestionId,
  openQuestionsModal,
  isMath,
  isCalc,
  onToggleCalculator,
}: IProps) => {
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [isReferenceOpen, setIsReferenceOpen] = useState(false);
  const {
    setHighlightToggle,
    highlightToggle,
  } = useHighlight();

  const questionIndex =
    questions.findIndex((question) => question.id === currentQuestionId) + 1;

  const navigate = useNavigate();
  const module = attempt.lastModule;
  const exit = () => navigate('/');
  const closeModals = () => {
    setIsReportOpen(false);
    setIsReferenceOpen(false);
  };

  return (
    <>
      <section
        className={'relative flex shrink-0 items-center justify-between bg-[#e7edf7] px-10 py-1.5'}
      >
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px]"
          style={attemptChromeDashStyle}
        />

        <div className={'flex'}>
          <div>
            <div>{`${attempt.test.name}: Section ${
              module.testSection.order + 1
            }, Module ${module.order + 1}`}</div>
            <button className={'text-sm text-gray'} onClick={openDirections}>
              Directions
            </button>
          </div>

          {isLoading && <Loading isSmall={true} />}
        </div>

        <Timer
          attemptId={attempt.id}
          setSeconds={setSeconds}
          durationInSeconds={seconds}
          onExpire={onTimerOver}
          newTimer={newTimer}
          setNewTimer={setNewTimer}
          stopTimer={stopTimer}
          setStopTimer={setStopTimer}
        />
        <div className="flex items-center gap-0.5">
          {!isMath && (
            <div
              className={cn(
                toolbarButtonClassName,
                'w-[6.9rem]',
                highlightToggle && activeToolbarButtonClassName,
              )}
              onClick={() => setHighlightToggle((prevState) => !prevState)}
            >
              <div className={cn(toolButtonIconRowClassName, 'gap-1')}>
                <MarkerLightDownIcon
                  width={18}
                  height={18}
                  fill={'currentColor'}
                />
                <PinnedNoteIcon
                  width={18}
                  height={18}
                  fill={'currentColor'}
                />
              </div>
              <div className={toolButtonLabelClassName}>Highlights & Notes</div>
            </div>
          )}

          {isMath && (
            <button
              className={cn(
                toolbarButtonClassName,
                'w-[5.3rem]',
                isCalc && activeToolbarButtonClassName,
              )}
              onClick={onToggleCalculator}
            >
              <div className={toolButtonIconRowClassName}>
                <BluebookCalculatorIcon className="h-[1.4rem] w-[1.4rem]" />
              </div>
              <div className={toolButtonLabelClassName}>Calculator</div>
            </button>
          )}

          {isMath && (
            <button
              className={cn(
                toolbarButtonClassName,
                isReferenceOpen && activeToolbarButtonClassName,
              )}
              onClick={() => setIsReferenceOpen(true)}
            >
              <div className={toolButtonIconRowClassName}>
                <ReferenceToolbarIcon />
              </div>
              <div className={toolButtonLabelClassName}>Reference</div>
            </button>
          )}

          <AttemptMoreMenu
            onExit={exit}
            onOpenQuestions={openQuestionsModal}
            onOpenReport={() => setIsReportOpen(true)}
            questionCountLabel={`${questionIndex}/${questions?.length}`}
          />
        </div>
      </section>

      <Modal
        title={'Question Report'}
        onClose={closeModals}
        isOpen={isReportOpen}
      >
        <QuestionReportForm questionId={questionId} close={closeModals} />
      </Modal>

      <ReferenceModal isOpen={isReferenceOpen} onClose={closeModals} />
    </>
  );
};
export default TestAttemptHeader;
